import '../modal/ui-modal-controller.js';
import styles from './ui-toggle-tab.css';
import { Digest, element } from '../../global/index.js';
import { createElement } from '../../global/ui-helpers.js';
import type { AttributeValue } from '../ui-element.type.js';
import { UIElement } from '../ui-element.js';
import { Component } from '../../decorators/component.decorator.js';
import { Attribute } from '../../decorators/attribute.decorator.js';
import { Child } from '../../decorators/child.decorator.js';

/**
 * @class
 * @memberof SharedComponents
 * @augments {UIElement}
 * @alias UIToggleTab
 * @element ui-toggle-tab
 * @classdesc Represents a class for <code>ui-toggle-tab</code> element.
 * It behaves like radio button under the hood and is meant to be used together in
 * group setting with the wrapper <code>ui-toggle-tabbar</code>.
 * @property {string} [label] {@attr label} Label of the element.
 * @property {string} [name] {@attr name} Name attribute of the element.
 * @property {string} [value] {@attr value} Value attribute of the element.
 * @property {boolean} [checked] {@attr checked} Checked attribute of the element.
 * @property {HTMLInputElement} inputElement - {@readonly} Shortcut to input type radio element.
 * @property {HTMLSpanElement} labelElement - {@readonly} Shortcut to input text label element.
 * @example
 * <ui-toggle-tabbar>
 *   <ui-toggle-tab label="Private" name="userProfile" value="private" checked></ui-toggle-tab>
 *   <ui-toggle-tab label="Business" name="userProfile" value="business"></ui-toggle-tab>
 *   <ui-toggle-tab label="VIP" name="userProfile" value="vip"></ui-toggle-tab>
 * </ui-toggle-tabbar>
 */
@Component({
    tag: 'ui-toggle-tab',
    styles,
})
export class UIToggleTab extends UIElement {
    @Attribute(String) label!: string;
    @Attribute(String) name!: string;
    @Attribute(String) value!: string;
    @Attribute(Boolean) checked!: boolean;

    @Child('.ui-toggle-tab__input') inputElement!: HTMLInputElement;
    @Child('.ui-toggle-tab__label') labelElement!: HTMLLabelElement;

    private _id: string = '';

    static get observedAttributes(): Array<string> {
        return ['label', 'checked'];
    }

    /**
     * @private
     */
    private get uuid(): string {
        if (!this._id) {
            this._id = Digest.randomId();
        }
        return this._id;
    }

    /**
     * @returns {Record<string, string>}
     * @private
     */
    private extractDataAttributes(): Record<string, string> {
        return [].filter
            .call(this.attributes, (attribute: Attr) => {
                if (attribute.name.startsWith('data-')) {
                    this.removeAttribute(attribute.name);
                    return true;
                }

                return false;
            })
            .reduce<Record<string, string>>((attributes, attribute: Attr) => {
                attributes[attribute.name] = attribute.value;
                return attributes;
            }, {});
    }

    /**
     * @returns {HTMLInputElement}
     * @private
     */
    private createInputElement(): HTMLInputElement {
        return createElement({
            tagName: 'input',
            classList: {
                'ui-toggle-tab__input': true,
            },
            attributes: {
                type: 'radio',
                id: this.uuid,
                name: this.name,
                value: this.value,
                ...this.extractDataAttributes(),
                ...(this.checked ? { checked: '' } : false),
            },
        });
    }

    /**
     * @inheritDoc
     */
    observeAttributes(
        name: string,
        _: AttributeValue,
        newValue: string | null
    ): void {
        // Issue in the tests, the element references are undefined and same in ibank
        if (this.state !== 'hydrated') {
            return;
        }

        switch (name) {
            case 'label':
                this.labelElement.innerText = newValue || '';
                break;
            case 'checked':
                if (newValue === null) {
                    this.inputElement.removeAttribute('checked');
                    break;
                }

                this.inputElement.setAttribute('checked', '');
                this.inputElement.dispatchEvent(
                    new Event('change', { bubbles: true })
                );

                break;
            default:
                break;
        }
    }

    /**
     * @inheritDoc
     */
    render(): void {
        this.append(
            element`<label for="${this.uuid}">${this.createInputElement()}<span class="ui-toggle-tab__label">${this.label}</span></label>`
        );
    }

    /**
     * @inheritDoc
     */
    hydrate(): void {
        Object.entries(this.extractDataAttributes()).forEach(
            ([dataAttributeKey, dataAttributeValue]: [
                string,
                string,
            ]): void => {
                this.inputElement.setAttribute(
                    dataAttributeKey,
                    dataAttributeValue
                );
            }
        );
    }
}
